import React from 'react';
import { Grid, Link, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import { routes } from 'utils/routing';
import Logo from 'components/common/Logo';
import Image from 'components/common/Image';
import { formatPhoneNumber } from 'utils/format';

interface IProps {
  organization: any;
  organizationMentions: any[];
  sponsors: any[];
  contactName?: string;
  contactPhone?: string;
  contactPhonePocNumber?: string;
  contactEmail?: string;
}

const useStyles = makeStyles()((theme) => ({
  logoImage: {
    margin: '0 56px 16px 0',
    [theme.breakpoints.up('md')]: {
      marginRight: 48,
    },
  },
  logoContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    margin: '0 auto -16px',
    maxWidth: 760,
    [theme.breakpoints.up('md')]: {
      padding: '0 26px 0 0',
    },
  },
  logoWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    marginRight: '-56px',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'flex-start',
    },
    [theme.breakpoints.up('md')]: {
      marginRight: '-48px',
    },
  },
}));

const EventContacts: React.FC<React.PropsWithChildren<IProps>> = ({
  organization,
  organizationMentions,
  contactName,
  contactPhone,
  contactPhonePocNumber,
  contactEmail,
  sponsors,
}) => {
  const { classes } = useStyles();
  const [t] = useTranslation('public');
  const phoneHref = `tel:+${contactPhone.replace(/\D/g, '')}`;
  const emailHref = `mailto:${contactEmail}`;
  const hosts = organizationMentions.concat();

  return (
    <Grid container spacing={6.125} style={{ paddingBottom: 10 }}>
      <Grid item xs={12}>
        <Grid container spacing={4.125}>
          <Grid item xs={12}>
            <Typography variant="h6">{t<string>('event.eventOrganization')}</Typography>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.logoContainer}>
              <div className={classes.logoWrapper}>
                <Link
                  key={organization.id}
                  target="_blank"
                  color="inherit"
                  underline="none"
                  href={routes.organizationProfile(organization.id).link}
                  className={classes.logoImage}
                >
                  <Logo xs={80} sm={80} md={88}>
                    <Image
                      variant="square"
                      src={organization.image?.original_url || organization.logo || '/static/img/defaultOrgLogo.png'}
                      alt={organization.title}
                      priority
                    />
                  </Logo>
                </Link>
              </div>
            </div>
          </Grid>
        </Grid>
      </Grid>
      {hosts.length > 0 && (
        <Grid item xs={12}>
          <Grid container spacing={4.125}>
            <Grid item xs={12}>
              <Typography variant="h6">{t<string>('event.eventOrganizationCoHost')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.logoContainer}>
                <div className={classes.logoWrapper}>
                  {hosts.map((item) => (
                    <Link
                      key={item.organization.id}
                      target="_blank"
                      color="inherit"
                      underline="none"
                      href={routes.organizationProfile(item.organization.id).link}
                      className={classes.logoImage}
                    >
                      <Logo xs={80} sm={80} md={88}>
                        <Image
                          variant="square"
                          src={
                            item.organization.image?.original_url ||
                            item.organization.logo ||
                            '/static/img/defaultOrgLogo.png'
                          }
                          alt={item.title}
                          priority
                        />
                      </Logo>
                    </Link>
                  ))}
                </div>
              </div>
            </Grid>
          </Grid>
        </Grid>
      )}
      {sponsors.length > 0 && (
        <Grid item xs={12}>
          <Grid container spacing={4.125}>
            <Grid item xs={12}>
              <Typography variant="h6">{t<string>('event.eventSponsors')}</Typography>
            </Grid>
            <Grid item xs={12}>
              <div className={classes.logoContainer}>
                <div className={classes.logoWrapper}>
                  {sponsors
                    .concat()
                    .sort((a: { order: number }, b: { order: number }) => a.order - b.order)
                    .map((item) => (
                      <Logo xs={80} sm={80} md={88} className={classes.logoImage}>
                        <Image src={item.image?.original_url} alt={item.image?.internal_title} priority />
                      </Logo>
                    ))}
                </div>
              </div>
            </Grid>
          </Grid>
        </Grid>
      )}
      <Grid item xs={12}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Typography variant="h6">{t<string>('event.eventContacts')}</Typography>
          </Grid>
          {contactName && (
            <Grid item xs={12} style={{ paddingTop: 33 }}>
              <Typography variant="body1" style={{ fontWeight: 500 }} component="p">
                {contactName}
              </Typography>
            </Grid>
          )}
          {(contactPhone || contactEmail) && (
            <Grid item xs={12} style={{ paddingTop: 24 }}>
              {contactPhone && (
                <div>
                  <Link href={phoneHref} variant="body1" style={{ fontWeight: 500 }} underline="none">
                    {formatPhoneNumber(contactPhone)}
                    {contactPhonePocNumber.length !== 0 && `; ${contactPhonePocNumber}`}
                  </Link>
                </div>
              )}
              {contactEmail && (
                <div>
                  <Link href={emailHref} variant="body1" style={{ fontWeight: 500 }} underline="none">
                    {contactEmail}
                  </Link>
                </div>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default EventContacts;
